import { ContentChild, Injector, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FiltersAbstractService } from './filters-abstract-service';
import { WaHelperService, ViewDataService, LoggerService } from 'world-archery-lib';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest, Subscription } from 'rxjs';
import { filter, map, tap, takeUntil, takeWhile } from 'rxjs/operators';
import { EntitiesStore } from '../stores/entities-store/entities-store.abstract';
import { Router, ActivatedRoute } from '@angular/router';
import { WaTableComponent } from '../wa-table/wa-table.component';



/**
 * This is an abstract table component that everty table into wa-app must be extended by the component that contain the table
 *
 * @export
 * @abstract
 * @class TableAbstractComponent
 * @template T
 * @template ParamsType
 */
export abstract class TableAbstractComponent<T = any, ParamsType = any> implements OnDestroy, OnInit {

  /**
   * The reference of the table with Angular component.
   *
   * @type {WaTableComponent}
   * @memberof TableAbstractComponent
   */
  @ViewChild('tableRef', {static:true}) tableRef : WaTableComponent;
  /**
   * Router table to be used for internal navigation in expand row.
   *
   * @type {Router}
   * @memberof TableAbstractComponent
   */
  _table_router: Router;


  /**
   * Activated route in table to be used for internal navigation in expand row.
   *
   * @type {ActivatedRoute}
   * @memberof TableAbstractComponent
   */
  _activated_route: ActivatedRoute;

  /**
   * Animation state of detail.
   *
   * @memberof TableAbstractComponent
   */
  animationDetailState = 'in';

  /**
   * A variable for identify if the row has been selected in details or another action
   *
   * @memberof TableAbstractComponent
   */
  selectedIndexVar = undefined;

  /**
   * A variable for identify if the row has been selected and open the details of table or scroll.
   *
   * @memberof TableAbstractComponent
   */
   selectedIndexNavigationVar = undefined;

  /**
   * A logger service that could be used
   *
   * @protected
   * @type {LoggerService}
   * @memberof TableAbstractComponent
   */
  protected logger: LoggerService

  /**
   * A view data service from the old library that could be used to get streams views
   *
   * @protected
   * @type {ViewDataService}
   * @memberof TableAbstractComponent
   */
  protected views: ViewDataService

  /**
   * A helper service for get data or values in a static wat hardcoded
   *
   * @protected
   * @type {WaHelperService}
   * @memberof TableAbstractComponent
   */
  protected waHelper: WaHelperService

  /**
   * A filter service that is used in a generic way for make the behaviour abstract
   *
   * @type {FiltersAbstractService}
   * @memberof TableAbstractComponent
   */
  public filter_service: FiltersAbstractService

  /**
   * Http client for making request
   *
   * @private
   * @type {HttpClient}
   * @memberof TableAbstractComponent
   */
  private http: HttpClient

  /**
   * A subject that shows when the data must be reloaded
   *
   * @type {BehaviorSubject<boolean>}
   * @memberof TableAbstractComponent
   */
  reload: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * A property that could be used for set a custom load message when
   *
   * @type {string}
   * @memberof TableAbstractComponent
   */
  customLoadMsg: string;

  /**
   * A property that could be used to indicate that if a box of table must be shown or not
   *
   * @type {boolean}
   * @memberof TableAbstractComponent
   */
  ShowNoResultsBox: boolean;
  /**
   * Creates an instance of TableAbstractComponent.
   * @param {Injector} injector
   * @param {FiltersAbstractService} filters_service
   * @param {EntitiesStore<T, ParamsType>} model
   * @memberof TableAbstractComponent
   */

  /**
   * An array of subscriptions for avoid memory leaks in constructions
   *
   * @type {Array<Subscription>}
   * @memberof TableAbstractComponent
   */
  subscriptions: Array<Subscription> = [];

  /**
   * The param name to be used in url when the data is opened.
   *
   * @memberof TableAbstractComponent
   */
  @Input()
  openedParam;

  /**
   * An input to allow navigation when the user select or deselect some row.
   *
   * @type {boolean}
   * @memberof TableAbstractComponent
   */
  @Input()
  withOpenNav: boolean = false;

  /**
   * A mark for check if the scroll was reached when navigating to row with parameter.
   *
   * @type {boolean}
   * @memberof TableAbstractComponent
   */
  scrollReached: boolean = false;
  private _Zone: NgZone;
  constructor(injector: Injector, filters_service: FiltersAbstractService, public model: EntitiesStore<T, ParamsType>) {
    this.logger = injector.get(LoggerService);
    this.views = injector.get(ViewDataService);
    this.waHelper = injector.get(WaHelperService);
    this.filter_service = filters_service;
    this.http = injector.get(HttpClient);
    this._table_router = injector.get(Router);
    this._activated_route = injector.get(ActivatedRoute);
    this._Zone = injector.get(NgZone)
    // Bind for not lose context https://stackoverflow.com/questions/35328652/angular-pass-callback-function-to-child-component-as-input
    this.sortClass = this.sortClass.bind(this);
  }



  /**
   * Method for perform custom navigation actions on data. It must be override if custom logic must be applied.
   *
   * @param {T[]} data The data of the model.
   * @param {WaTableComponent} table_ref The table reference passed as argument.
   * @returns {{mobile:HTMLElement, desktop:HTMLElement}} Return an Object to perform scroll to the row of the table.
   * @memberof TableAbstractComponent
   */
  performCustomNavigation(data: T[], table_ref:WaTableComponent) : {mobile:HTMLElement, desktop:HTMLElement}{
    return {mobile:undefined, desktop:undefined};
  }

  /**
   * {@inheritdoc}
   *
   * @memberof TableAbstractComponent
   */
  ngOnInit() {
    const subRowParam = this._activated_route.snapshot.queryParams[this.openedParam];
    if (subRowParam) {
      this.selectedIndexNavigationVar = subRowParam;
      this.subscriptions.push(
        this.model.dataStream.pipe(
          // Filter first data
          filter(value => value != undefined),
          // filter(value => value.length > this.selectedIndexVar),
          // Take while the scroll is not reached.
          takeWhile(value => !this.scrollReached),
          tap(a => {
            // Timeout to let the ngZone construct elements.
            this._Zone.run(() => {
              setTimeout(() => {
                const {mobile, desktop} = this.performCustomNavigation(this.model.data, this.tableRef);
                  const element = desktop ? desktop : document.getElementById(`${this.openedParam}_${this.selectedIndexVar}`)
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                    this.scrollReached = true;
                  }
                  const elementMobile = mobile ? mobile : document.getElementById(`${this.openedParam}_${this.selectedIndexVar}_mobile`)
                  if (elementMobile) {
                    elementMobile.scrollIntoView({ behavior: 'smooth' });
                    this.scrollReached = true;
                  }

              }, 400)

            });


          }

          )
        ).subscribe()
      )
    } else {
      // The scroll has ended.
      this.scrollReached = true;
    }
  }

  /**
   * A getter for know if there's more data to request and show or hide button
   *
   * @readonly
   * @memberof TableAbstractComponent
   */
  get request_more_data() {
    return this.model.requestMore;
  }

  /**
   * A getter for return the data that must be binded to the view
   *
   * @readonly
   * @memberof TableAbstractComponent
   */
  get data() {
    return this.model.data;
  }

  /**
   * A getter to know if the model is loading and have pending request
   *
   * @readonly
   * @memberof TableAbstractComponent
   */
  get loading() {
    return this.model.loadingRef;
  }

  /**
   * A getter that returns the pagination data from model and could be used into the view
   *
   * @readonly
   * @memberof TableAbstractComponent
   */
  get pagination() {
    return this.model.pagination;
  }

  /**
   * A references to the service for use into the view with a simple and generic name
   *
   * @readonly
   * @memberof TableAbstractComponent
   */
  get service() {
    return this.filter_service;
  }

  /**
   * A method to change sort for any value
   *
   * @param {*} category
   * @memberof TableAbstractComponent
   */
  changeSort(category: any) {
    if (category === undefined) {
      this.filter_service.sortLocator.sortingType = undefined;
      this.filter_service.sortLocator.asc = true;
    } else {
      if (category === this.filter_service.sortLocator.sortingType) {
        if (this.filter_service.sortLocator.asc === false) {
          this.filter_service.sortLocator.sortingType = undefined;
        }
        this.filter_service.sortLocator.asc = !this.filter_service.sortLocator
          .asc;
      } else {
        this.filter_service.sortLocator.sortingType = category;
        this.filter_service.sortLocator.asc = true;
      }
    }
    this.filter_service.modifyOrderValue();
  }

  /**
   * A method to get the class of the default header into the table
   *
   * @returns
   * @memberof TableAbstractComponent
   */
  rankingSort() {
    if (this.filter_service.sortLocator.sortingType === undefined) {
      return 'asc';
    } else {
      return 'desc';
    }
  }

  /**
   * A method to return the sort class of the header.
   *
   * @param {*} category
   * @returns
   * @memberof TableAbstractComponent
   */
  sortClass(category: any) {

    if (this.filter_service.sortLocator.sortingType !== category) {
      return '';
    }
    return this.filter_service.sortLocator.asc === true ? 'asc' : 'desc';
  }

  /**
   * A method to perform a load of the model
   *
   * @memberof TableAbstractComponent
   */
  loadMore() {
    this.model.loadingRef = true;
    this.model.requestMore = true;
    this.reload.next(true);
  }

  /**
   * A method to confirm if there's more data in the model
   *
   * @returns {boolean}
   * @memberof TableAbstractComponent
   */
  areMoreData(): boolean {
    return this.pagination != undefined && this.data.length !== this.pagination.totalResults;
  }


  /**
   * A method for select an Index of a table for show details
   *
   * @param {number} index The index that should be used in table.
   * @param {(string|number)} [custom_index_for_navigation] A custom index for navigation if we want to have both splitted.
   * @param {boolean} [force_set_index] A flag for force to set the index and avoid undefined when multiple calls are performed.
   * @memberof TableAbstractComponent
   */
  selectIndex(index: number, custom_index_for_navigation?:string|number, force_set_index?:boolean) {
    const custom_index = this.selectedIndexNavigationVar === custom_index_for_navigation ? undefined : custom_index_for_navigation;
    if (index === this.selectedIndexVar && force_set_index == undefined) {
      this.selectedIndexVar = undefined;

    } else {
      this.selectedIndexVar = index;
    }
      this.navigateWithParam(custom_index ? custom_index : this.selectedIndexVar);
  }

  navigateWithParam(custom_index_for_navigation?:string|number) {
    if (this.openedParam && this.withOpenNav) {
      this.selectedIndexNavigationVar = custom_index_for_navigation ? custom_index_for_navigation : this.selectedIndexVar;
      this._table_router.navigate([], { queryParams: { [this.openedParam]: custom_index_for_navigation}, queryParamsHandling: 'merge' })
    }
  }

  /**
   * Check if index is selected in the table
   *
   * @param {number} index
   * @returns
   * @memberof TableAbstractComponent
   */
  isIndexSelected(index: number) {
    return index === this.selectedIndexVar;
  }

  /**
   *
   * @inheritdoc
   * @memberof TableAbstractComponent
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach(value => value.unsubscribe());
  }
}
